"use client";

import { useLayoutEffect } from "react";

import type { Locale } from "@/i18n/routing";
import type { TextDirection } from "@/lib/locale/direction";

/**
 * Keeps `<html dir>` in sync (including client navigations between locales).
 * Intentionally does not restore a previous value on unmount — the bootstrap
 * script and/or the next locale's effect own the attribute.
 */
type DocumentDirectionProps = {
  dir: TextDirection;
  locale: Locale;
};

export function DocumentDirection({ dir, locale }: DocumentDirectionProps) {
  useLayoutEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", locale);
  }, [dir, locale]);

  return null;
}

/**
 * Blocking inline script so `<html dir>` is set before hydration when the
 * root layout could not yet resolve locale. Portals read direction from `<html>`.
 */
export function DocumentDirectionScript({
  dir,
  locale,
}: DocumentDirectionProps) {
  const code = `document.documentElement.setAttribute("dir",${JSON.stringify(dir)});document.documentElement.setAttribute("lang",${JSON.stringify(locale)});`;
  return <script dangerouslySetInnerHTML={{ __html: code }} />;
}
